/*
  Расхождения файлов с портфелем: список, отметки и выбор человека.

  Решение владельца 01.10.2026 (строка 178 реестра компромиссов): адрес и вид
  собственности счёта меняет только выбор человека. Здесь состояние и запросы,
  разметка - в `Divergences.jsx`.

  Заранее отмечено то, что сервер назвал `suggested`: расхождение по существу
  без последствий для дома и участка (макет А владельца). Отметки остальных
  строк ставит человек.

  Список перечитывается, когда меняется `revision` вкладки: прочитан новый файл
  или кончился перенос.

  🔴 Замена не трогает строк, которых человек не видел (01.10.2026, проверка
  перед выкладкой). Раньше заранее отмечались ВСЕ строки `suggested`, а
  показывались первые двадцать: человек снимал отметки с видимых, и «Заменить
  (40)» меняло квартиры в счетах, которых он не открывал. Теперь:
  - заранее отмечается строка, только когда она впервые ПОКАЗАНА (`seen`);
  - на сервер уходят и в кнопках считаются только отмеченные из показанных;
  - перечитывание списка не возвращает снятых отметок: строка, которую человек
    уже видел, остаётся как он её оставил, исчезнувшая уходит из отметок.
*/

import { useCallback, useEffect, useState } from 'react'

import { api } from '../../api/client.js'

export const PAGE = 20

// Строка - одно значение одного счёта; первое сведение её и называет.
export const keyOf = (item) => item.fact_ids[0]

/*
  Отметки после того, как изменился список или его показанная часть. Чистая
  функция: строка, впервые попавшая в показанные, отмечается по `suggested`;
  уже виденная сохраняет выбор человека; исчезнувшая забывается целиком - если
  вернётся, она для человека новая.
*/
export function reconcile({ chosen, seen }, items, shown) {
  const present = new Set(items.map(keyOf))
  const nextChosen = new Set(chosen.filter((key) => present.has(key)))
  const nextSeen = new Set(seen.filter((key) => present.has(key)))
  items.slice(0, shown).forEach((item) => {
    const key = keyOf(item)
    if (nextSeen.has(key)) return
    nextSeen.add(key)
    if (item.suggested) nextChosen.add(key)
  })
  return { chosen: [...nextChosen], seen: [...nextSeen] }
}

const EMPTY = { items: [], shown: PAGE, chosen: [], seen: [] }

export default function useDivergences(revision, onDecided) {
  const [list, setList] = useState(EMPTY)
  const [state, setState] = useState({ busy: false, error: null, loadError: null })

  const reload = useCallback(async () => {
    try {
      const found = await api.uploadDivergences()
      setList((current) => ({ ...current, items: found, ...reconcile(current, found, current.shown) }))
      setState((current) => ({ ...current, loadError: null }))
    } catch (failure) {
      setState((current) => ({ ...current, loadError: failure }))
    }
  }, [])

  useEffect(() => {
    reload()
  }, [revision, reload])

  const visible = list.items.slice(0, list.shown)
  const visibleKeys = visible.map(keyOf)
  const chosen = visibleKeys.filter((key) => list.chosen.includes(key))

  const showMore = (count) =>
    setList((current) => {
      const shown = current.shown + count
      return { ...current, shown, ...reconcile(current, current.items, shown) }
    })

  const toggle = (key) =>
    setList((current) => ({
      ...current,
      chosen: current.chosen.includes(key)
        ? current.chosen.filter((item) => item !== key)
        : [...current.chosen, key],
    }))

  // Отметить или снять все ПОКАЗАННЫЕ строки - не весь список.
  const chooseShown = (on) =>
    setList((current) => {
      const rest = current.chosen.filter((key) => !visibleKeys.includes(key))
      return { ...current, chosen: on ? [...rest, ...visibleKeys] : rest }
    })

  const decide = async (apply) => {
    const ids = visible.filter((item) => chosen.includes(keyOf(item))).flatMap((item) => item.fact_ids)
    setState({ busy: true, error: null, loadError: null })
    try {
      await api.decideDivergences(ids, apply)
      setState({ busy: false, error: null, loadError: null })
      await reload()
      onDecided?.()
    } catch (failure) {
      setState({ busy: false, error: failure, loadError: null })
    }
  }

  return { items: list.items, visible, chosen, showMore, toggle, chooseShown, decide, ...state }
}
